import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import FragmentBlock, { Remarks } from './Fragment';
import { TurnQuestions, ActiveTurnAudio, soundRemarkIds, owesAnswer } from './TurnState';
import TaskChat from './TaskChat';
import ProposalBlock from './Proposal';
import TurnEdits from './Edit';
import Assembly from './Assembly';
import { takeStamp } from '../../shared/takeStamp';
import TurnTextResults from './TurnTextResults';
import { fragmentLabel } from './turnStatus';

// One speaker turn opened from the by-turns list, on the right where a card
// opens — the one place a turn's sound is discussed and decided. Left, always
// in this order: the questions a person owes on the turn (TurnState.js),
// every remark with one sentence on where it stands, the edits and the
// fragment to judge (Edit.js, Fragment.js) or, once Edit was pressed, the
// Draft — the assembly of takes (Assembly.js) — and the turn as the active
// take sounds. Right: the chat of the turn; «Run turn» asks its agent to go
// through the remarks — answers first, then the re-voice or the assembly.

const fmtTime = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function TurnPanel({ questId, locationUuid, field, turnIndex, usernames, currentUser, onOpenCard, onClose }) {
  const [f, setF] = useState(null);
  const [chat, setChat] = useState(null);       // {task, messages}
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(null);
  const [editing, setEditing] = useState(false);   // Edit pressed: the Draft is shown even before the server lists it
  const [noteText, setNoteText] = useState('');
  const seqRef = useRef(0);
  const timerRef = useRef(null);
  const chatIdRef = useRef(null);
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const r = await api.fieldTurns(questId, locationUuid, field);
      if (seq !== seqRef.current) return;
      setF((r.fields || [])[0] || null);
    } catch (e) { if (seq === seqRef.current) setError(e.message); }
  }, [questId, locationUuid, field]);
  // The chat of the turn: created on first open, nothing started.
  // Several events at once (the reply, the run, the card) each read the chat;
  // only the latest read is shown, so a slower earlier one cannot put back a
  // chat without the reply.
  // Two counters: the full read (task, runs, messages) and the light read of
  // the messages alone (after a send, on a message event) — the light one is
  // back in a fraction of a second while the full one waits behind the
  // agent's start, so each applies only what no later read has overtaken.
  const chatSeqRef = useRef(0);
  const msgSeqRef = useRef(0);
  const chatIdOf = useCallback(async () => {
    const id = chatIdRef.current || (await api.turnChat(questId, { locationUuid, field, turn: turnIndex, start: false })).task.id;
    chatIdRef.current = id;
    return id;
  }, [questId, locationUuid, field, turnIndex]);
  const loadChat = useCallback(async () => {
    const seq = ++chatSeqRef.current;
    const mseq = ++msgSeqRef.current;
    try {
      const id = await chatIdOf();
      const r = await api.read(id);
      if (seq !== chatSeqRef.current && mseq !== msgSeqRef.current) return;
      setChat((prev) => ({
        task: seq === chatSeqRef.current || !prev ? r.task : prev.task,
        runs: seq === chatSeqRef.current || !prev ? (r.runs || []) : prev.runs,
        messages: mseq === msgSeqRef.current || !prev ? r.messages : prev.messages,
      }));
    } catch (e) { if (seq === chatSeqRef.current) setError(e.message); }
  }, [chatIdOf]);
  const loadMessages = useCallback(async () => {
    const mseq = ++msgSeqRef.current;
    try {
      const id = await chatIdOf();
      const r = await api.messages(id);
      if (mseq !== msgSeqRef.current) return;
      setChat((prev) => (prev ? { ...prev, messages: r.messages } : prev));
    } catch (e) { if (mseq === msgSeqRef.current) setError(e.message); }
  }, [chatIdOf]);
  useEffect(() => { setF(null); setChat(null); setEditing(false); chatIdRef.current = null; load(); loadChat(); }, [load, loadChat]);
  useEffect(() => {
    const schedule = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => { timerRef.current = null; load(); }, 400); };
    const onEv = (e) => {
      schedule();
      const d = e.detail || {};
      const id = chatIdRef.current;
      if (!id) return;
      if ((d.task && d.task.id === id) || (d.run && d.run.task_id === id)) loadChat();
      else if (d.message && d.message.task_id === id) loadMessages();
    };
    const onResync = () => { schedule(); loadChat(); };
    window.addEventListener('qv:tasks', onEv);
    window.addEventListener('qv:resync', onResync);
    window.addEventListener('qv:poll', onResync);   // the stream brings nothing: read on App's timer
    return () => { window.removeEventListener('qv:tasks', onEv); window.removeEventListener('qv:resync', onResync); window.removeEventListener('qv:poll', onResync); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [load, loadChat, loadMessages]);

  if (error && !f) return <div className="tk-card"><div className="tk-error">{error}</div></div>;
  if (!f) return <div className="tk-card"><div className="tk-muted">Loading the turn…</div></div>;
  const t = f.turns.find((x) => x.i === turnIndex);
  if (!t) return <div className="tk-card"><div className="tk-muted">Point {f.pointNo}, field {field}, no longer has turn {turnIndex + 1}. After the text changed, {f.turns.length} turns remain. Select the intended turn from the list.</div></div>;
  const revoice = async () => {
    setBusy(true); setError(null);
    // The button stays «Starting…» until the chat is read again with the
    // queued turn in it; from then on agentBusy holds it.
    try { await api.turnChat(questId, { locationUuid, field, turn: turnIndex, start: true }); await loadChat(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const passBusy = f.pass && ['queued', 'running'].includes(f.pass.status);
  // The pass keeps this turn's fragment as soon as it is generated: once it is
  // here, the pass (measuring, or voicing other turns) no longer holds Run turn
  // — a second run would only make another take, which the hint says.
  const runHeld = passBusy && !t.fragment;
  // The turn's agent is queued or answering: a second press would be dropped
  // by the server anyway. Read from the runs, not the card's status — a turn
  // cut short by a restart is claimed again once its lease runs out, while a
  // card status could stay «running» and hold the button for good.
  const agentRun = chat && (chat.runs || []).find((r) => r.task_id === chat.task.id && (r.recipe_id === 'agent' || !r.recipe_id) && ['queued', 'running'].includes(r.status));
  const agentBusy = !!agentRun;
  const fragNote = t.fragment ? `A take is already available: ${takeStamp(t.fragment, { reused: t.fragment.reused })}.${t.fragment.hasTimings === false ? ' You can listen now; a precise splice awaits word boundaries.' : ''} Run turn sends the remarks to the agent again. In the chat, specify whether you want another take or an assembly from existing recordings.` : null;
  // Questions the person still owes on this turn (a stress to pick, an answer
  // to a card's agent): the re-voice fixes everything in one take, so it waits
  // for them rather than spending a model call on asking again.
  const owedCount = new Set((t.cards || []).filter(owesAnswer).map((c) => c.taskId)).size;
  // Remarks on this turn not settled yet: «Mark all as already correct» marks every one of
  // them by hand (no re-voice), the way Accept would after a fragment.
  const unsettled = (t.cards || []).filter((c) => !c.accepted && c.phase !== 'done');
  // Every remark on the turn accepted and no fragment waiting: a run would find
  // an empty desk, so the button waits for a new remark.
  const allAccepted = (t.cards || []).length > 0 && unsettled.length === 0 && !t.fragment && !t.draft;
  const markAll = async () => {
    setBusy(true); setError(null);
    try { await api.turnMark(questId, { locationUuid, field, turns: [turnIndex] }); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const showDraft = !!t.draft || editing;
  const openDraft = async (fragment) => {
    setBusy(true); setError(null);
    try { await api.draftOpen(questId, { locationUuid, field, turn: turnIndex, baseVersionId: fragment ? fragment.fragmentVersionId : null }); setEditing(true); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const notes = (t.notes || []).filter((n) => n.status !== 'dropped');
  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setBusy(true); setError(null);
    try { await api.turnNoteAdd(questId, { locationUuid, field, turn: turnIndex, text }); setNoteText(''); await load(); await loadChat(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const noteStatus = async (n, status) => {
    setBusy(true); setError(null);
    try { await api.turnNoteUpdate(questId, n.id, { locationUuid, field, turn: turnIndex, status }); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="tk-card tk-turn-panel">
      <div className="tk-card-status">
        {t.state === 'no_take' ? <span className="tk-chip st-needs_attention">no take</span> : null}
        {t.requested ? <span className="tk-chip st-queued">asked for</span> : null}
        {t.fragment ? <span className={`tk-chip ${t.fragment.acceptWhenMeasured ? 'st-queued' : t.fragment.acceptError ? 'st-failed' : 'st-ready'}`}>{fragmentLabel(t.fragment)}</span> : null}
        {t.draft ? <span className="tk-chip st-ready">Draft open</span> : null}
        {t.accepted ? <span className="tk-chip st-done">accepted</span> : null}
        <span className="tk-grow" />
        {onClose ? <button type="button" className="tk-btn tk-btn-ghost tk-btn-small" onClick={onClose} aria-label="Close the turn">✕</button> : null}
      </div>
      <h2 className="tk-title">Point {f.pointNo}{f.pointName ? ` · ${f.pointName}` : ''} · {field} · {t.label}</h2>
      {t.chat && t.chat.wordsMatch === false ? <div className="tk-uncert">⚠ The turn text changed after this chat was created. Earlier messages refer to the previous text; check which words the remarks concern.</div> : null}
      <div className="tk-actions">
        <button type="button" className="tk-btn tk-btn-primary" onClick={revoice} disabled={busy || agentBusy || runHeld || allAccepted}
          title={agentBusy ? 'The agent is already going through this turn.' : allAccepted ? 'All remarks on this turn are marked as resolved.' : fragNote || 'The turn agent handles its remarks, text and recording here.'}>{agentBusy ? (agentRun.status === 'queued' ? 'Turn queued…' : 'Agent is on this turn…') : busy ? 'Starting…' : 'Run turn'}</button>
        {unsettled.length && !t.fragment && !t.draft ? <button type="button" className="tk-btn tk-btn-ghost" onClick={markAll} disabled={busy || passBusy}
          title={`Marks all ${unsettled.length} remark${unsettled.length === 1 ? '' : 's'} on this turn (${t.label}) as already correct — on this turn only: you listened and the active take${f.active ? ` (${takeStamp(f.active)})` : ''} already reads them right, so the turn is not voiced again. Where these remarks sit on other turns they stay open. The cards stay open until Mark done. Each mark can be taken back with Unmark under the remark.`}>Mark all as already correct</button> : null}
        {!showDraft ? <button type="button" className="tk-btn tk-btn-ghost" onClick={() => openDraft(t.fragment)} disabled={busy || t.state === 'no_take'} title="Open a Draft of this turn: the result is built from several takes (the new take, the source take, any other take of the field) range by range; the active take changes only on Accept of the assembly.">Edit</button> : null}
        {passBusy ? <span className="tk-muted">{f.pass.status === 'queued' ? 'The voiceover request is queued.' : 'Voiceover for this text is still running.'}{t.fragment ? ' This turn’s take is already saved.' : ''}{f.pass.stage ? ` Step: ${f.pass.stage}.` : ''}</span> : null}
        {!passBusy && t.fragment && !showDraft ? <span className="tk-muted">{t.fragment.acceptWhenMeasured ? 'Your Accept is saved. The system is applying the take; no further decision is needed.' : t.fragment.acceptError ? 'Applying the chosen take failed. See the error below.' : 'A take is available below to compare with the current recording.'}</span> : null}
        {!passBusy && owedCount ? <span className="tk-muted">Review the pending decisions below or discuss them with the turn agent here.</span> : null}
        {!passBusy && !owedCount && allAccepted ? <span className="tk-muted">All remarks on this turn are marked as resolved.</span> : null}
      </div>
      <div className="tk-card-cols">
        <section className="tk-section tk-col-where">
          <TurnTextResults questId={questId} locationUuid={locationUuid} field={field} turnIndex={turnIndex} onChanged={async () => { await load(); await loadChat(); }} />
          <TurnQuestions turn={t} field={field} onOpenCard={onOpenCard} onChanged={load} onAnswer={async (c, text) => { const id = await chatIdOf(); await api.message(id, `@autoexecutor По замечанию «${c.title}» (${c.taskId}): ${text}`); await loadMessages(); await loadChat(); }} />
          {t.cards.length ? (
            <Remarks cards={(() => { const seen = new Set(); return t.cards.filter((c) => (seen.has(c.taskId) ? false : seen.add(c.taskId))); })()} hover={hover} onHover={setHover} onOpenCard={onOpenCard} turn={t} field={f} onChanged={load} questId={questId} />
          ) : <div className="tk-muted">No remark cards are linked to this turn yet.</div>}
          {!showDraft && notes.length ? (
            <div className="tk-notes">
              <div className="tk-block-label">Notes on this turn <span className="tk-muted">· remarks without a card; the agent reads them with the cards</span></div>
              {notes.map((n) => <div key={n.id} className="tk-note">{n.place ? <strong>«{n.place.text}»: </strong> : null}<span>{n.text}</span><span className="tk-muted">· {n.by}, {fmtTime(n.at)}</span>{n.status === 'fixed' ? <span className="tk-chip st-done">marked as fixed</span> : <button type="button" className="tk-linkbtn" onClick={() => noteStatus(n, 'fixed')} disabled={busy}>mark fixed</button>}</div>)}
            </div>
          ) : null}
          {!showDraft ? (
            <div className="tk-row">
              <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="A remark on this turn, kept here without a card (the agent reads it before the next recording)" aria-label="Remark on the turn" onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
              <button type="button" className="tk-btn tk-btn-small" onClick={addNote} disabled={busy || !noteText.trim()}>Add remark</button>
            </div>
          ) : null}
          <TurnEdits turn={t} locationUuid={locationUuid} field={field} onOpenCard={onOpenCard} onChanged={load} />
          {showDraft ? (
            <Assembly questId={questId} locationUuid={locationUuid} field={field} turnIndex={turnIndex} draftId={t.draft ? t.draft.id : null} onOpenCard={onOpenCard} onChanged={async () => { await load(); await loadChat(); }} onClosed={() => { setEditing(false); load(); }} hover={hover} />
          ) : null}
          {!showDraft && (t.fragment || t.accepted) ? (
            <FragmentBlock questId={questId} locationUuid={locationUuid} field={field} turn={t} activeVersionId={f.active ? f.active.versionId : null} onOpenCard={onOpenCard} onChanged={load} hideRemarks onEdit={t.fragment ? (frag) => openDraft(frag) : null} />
          ) : null}
          {!t.fragment && !showDraft ? <ActiveTurnAudio questId={questId} locationUuid={locationUuid} field={field} turnIndex={turnIndex} activeVersionId={f.active ? f.active.versionId : null} active={f.active} hover={hover} soundIds={soundRemarkIds(t)} /> : null}
          {error ? <div className="tk-error">{error}</div> : null}
        </section>
        <section className="tk-section tk-col-chat">
          <h3>Chat of this turn <span className="tk-muted">· the one dialog of every remark on it</span></h3>
          {chat ? (
            <TaskChat taskId={chat.task.id} messages={chat.messages} usernames={usernames || []} currentUser={currentUser} busy={busy}
              renderAttachment={(m) => (m.meta && m.meta.kind === 'card_proposal' ? <ProposalBlock taskId={chat.task.id} message={m} onOpenCard={onOpenCard} onChanged={loadChat} /> : null)}
              onSend={async (body) => { const r = await api.message(chat.task.id, body); loadMessages(); loadChat(); return r.message; }} />
          ) : <div className="tk-muted">Opening the chat…</div>}
        </section>
      </div>
    </div>
  );
}
